import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Search, FileSearch, Mail, GitCompareArrows, Calculator, Copy, Check, AlertTriangle, Save, BookOpen, Scale, Lightbulb, ShieldAlert, FileText, Upload, FileSpreadsheet } from "lucide-react";
import { useI18n } from "../../i18n";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const NAVY = "hsl(217_63%_15%)";
const GREEN = "hsl(158_64%_28%)";

function CopyBtn({ text }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  if (!text) return null;
  return (
    <button
      data-testid="tk-copy-btn"
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(158_64%_28%)] hover:underline"
    >
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {done ? t("tk_copied") : t("tk_copy")}
    </button>
  );
}

function RunButton({ onClick, loading, testid }) {
  const { t } = useI18n();
  return (
    <Button data-testid={testid} onClick={onClick} disabled={loading} className="rounded-full text-white" style={{ backgroundColor: NAVY }}>
      <Sparkles className="h-4 w-4 me-1.5" />{loading ? t("tk_running") : t("tk_run")}
    </Button>
  );
}

const Panel = ({ children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5 space-y-3">{children}</div>
);

function TaxSelect({ value, onChange, testid }) {
  const { t } = useI18n();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-48 h-9"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="vat">{t("tk_tax_vat")}</SelectItem>
        <SelectItem value="excise">{t("tk_tax_excise")}</SelectItem>
        <SelectItem value="rett">{t("tk_tax_rett")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function SaveToCase({ kind, title, content }) {
  const { t, lang } = useI18n();
  const [cases, setCases] = useState([]);
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/cases").then((r) => setCases((r.data || []).filter((c) => !["completed", "closed"].includes(c.status)))).catch(() => {});
  }, []);
  const save = async () => {
    if (!sel || !content) return;
    setSaving(true);
    try { await api.post(`/cases/${sel}/drafts`, { kind, title, content, lang }); toast.success(t("tk_draft_saved")); setSel(""); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  if (!content) return null;
  return (
    <div className="mt-3 pt-3 border-t border-border flex flex-wrap items-center gap-2" data-testid="tk-save-to-case">
      {cases.length === 0 ? (
        <span className="text-xs text-muted-foreground">{t("tk_no_open_cases")}</span>
      ) : (
        <>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="tk-draft-case-select" className="w-56 h-9"><SelectValue placeholder={t("tk_select_case")} /></SelectTrigger>
            <SelectContent>
              {cases.map((c) => <SelectItem key={c.id} value={c.id}>{(c.reference ? c.reference + " · " : "") + (c.title || c.service_type || c.id)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="tk-save-draft-btn" onClick={save} disabled={!sel || saving} variant="outline" className="rounded-full h-9">
            <Save className="h-4 w-4 me-1.5" />{t("tk_save_draft")}
          </Button>
        </>
      )}
    </div>
  );
}

function ConfBadge({ level }) {
  const { t } = useI18n();
  if (!level) return null;
  const map = { high: "bg-[hsl(158_64%_92%)] text-[hsl(158_64%_26%)]", medium: "bg-[hsl(38_92%_90%)] text-[hsl(30_80%_38%)]", low: "bg-[hsl(354_78%_94%)] text-[hsl(354_60%_45%)]" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${map[level] || map.medium}`}>{t("tk_confidence")}: {level}</span>;
}

/* ------------ Research ------------ */
function ResearchTool({ model }) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("private");
  const [tax, setTax] = useState("vat");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!q.trim()) return;
    setLoading(true); setRes(null);
    try { const r = await api.post("/toolkit/research", { question: q, sector_type: sector, tax_type: tax, model }); setRes(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const answer = res && (lang === "ar" ? res.answer_ar : res.answer_en);
  const points = res && (lang === "ar" ? res.key_points_ar : res.key_points_en) || [];
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel testid="tk-research-input">
        <Textarea data-testid="tk-research-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("tk_research_ph")} className="min-h-[140px]" />
        <div className="flex items-center gap-2 flex-wrap">
          <TaxSelect value={tax} onChange={setTax} testid="tk-research-tax" />
          <Select value={sector} onValueChange={setSector}>
            <SelectTrigger data-testid="tk-research-sector" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="private">{t("tk_sector_private")}</SelectItem>
              <SelectItem value="government">{t("tk_sector_government")}</SelectItem>
              <SelectItem value="nonprofit">{t("tk_sector_nonprofit")}</SelectItem>
            </SelectContent>
          </Select>
          <RunButton onClick={run} loading={loading} testid="tk-research-run" />
        </div>
      </Panel>
      <Panel testid="tk-research-output">
        {!res && <p className="text-sm text-muted-foreground">{t("tk_empty")}</p>}
        {res && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold" style={{ color: NAVY }}>{t("tk_answer")}</span>
              <div className="flex items-center gap-3"><ConfBadge level={res.confidence} /><CopyBtn text={answer} /></div>
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{answer}</p>
            {points.length > 0 && (
              <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_key_points")}</div>
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">{points.map((p, i) => <li key={i}>• {p}</li>)}</ul></div>
            )}
            {res.regulatory_references?.length > 0 && (
              <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_references")}</div>
                <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                  {res.regulatory_references.map((r, i) => <li key={i}>• {r.article} — {lang === "ar" ? r.title_ar : r.title_en}</li>)}
                </ul></div>
            )}
            <SaveToCase kind="research" title={q.slice(0, 80)} content={answer} />
          </>
        )}
      </Panel>
    </div>
  );
}

/* ------------ Document ------------ */
function DocumentTool({ model }) {
  const { t, lang } = useI18n();
  const [text, setText] = useState("");
  const [dtype, setDtype] = useState("tax_invoice");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!text.trim()) return;
    setLoading(true); setRes(null);
    try { const r = await api.post("/toolkit/document", { text, doc_type: dtype, model }); setRes(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name); setLoading(true); setRes(null);
    const fd = new FormData(); fd.append("file", f); fd.append("doc_type", dtype);
    try { const r = await api.post("/toolkit/document-file", fd, { headers: { "Content-Type": "multipart/form-data" } }); setRes(r.data); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); e.target.value = ""; }
  };
  const types = ["tax_invoice", "simplified_invoice", "credit_note", "debit_note", "contract", "import_declaration", "export_doc"];
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel testid="tk-doc-input">
        <Select value={dtype} onValueChange={setDtype}>
          <SelectTrigger data-testid="tk-doc-type" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{types.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".pdf,image/png,image/jpeg,image/webp" hidden onChange={onFile} data-testid="tk-doc-file-input" />
          <Button data-testid="tk-doc-upload-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={loading} className="rounded-full">
            <Upload className="h-4 w-4 me-1.5" />{t("tk_doc_upload")}
          </Button>
          {fileName && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{fileName}</span>}
        </div>
        <div className="text-[11px] text-muted-foreground text-center">{t("tk_doc_or")}</div>
        <Textarea data-testid="tk-doc-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("tk_doc_ph")} className="min-h-[140px]" />
        <RunButton onClick={run} loading={loading} testid="tk-doc-run" />
      </Panel>
      <Panel testid="tk-doc-output">
        {!res && <p className="text-sm text-muted-foreground">{t("tk_empty")}</p>}
        {res && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold" style={{ color: NAVY }}>{t("tk_doc_result")}: {res.result}</span>
              <span className="text-[11px] text-muted-foreground">{res.document_type}</span>
            </div>
            {res.fields && (
              <div><div className="text-xs font-medium text-foreground">{t("tk_doc_fields")}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs">
                  {Object.entries(res.fields).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="font-medium text-foreground truncate">{v}</span></div>
                  ))}
                </div></div>
            )}
            {res.missing_fields?.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-[hsl(30_80%_38%)]"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div><span className="font-medium">{t("tk_doc_missing")}: </span>{res.missing_fields.join("، ")}</div></div>
            )}
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{lang === "ar" ? res.notes_ar : res.notes_en}</p>
          </>
        )}
      </Panel>
    </div>
  );
}

/* ------------ Draft ------------ */
function DraftTool({ model }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ audience: "client", purpose: "", points: "", tone: "professional", context: "" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const run = async () => {
    if (!form.purpose && !form.points) return;
    setLoading(true); setRes(null);
    try { const r = await api.post("/toolkit/draft", { ...form, model }); setRes(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const subject = res && (lang === "ar" ? res.subject_ar : res.subject_en);
  const body = res && (lang === "ar" ? res.body_ar : res.body_en);
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel testid="tk-draft-input">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground">{t("tk_draft_audience")}</label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="tk-draft-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">{t("tk_aud_client")}</SelectItem>
                <SelectItem value="zatca">{t("tk_aud_zatca")}</SelectItem>
                <SelectItem value="internal">{t("tk_aud_internal")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t("tk_draft_tone")}</label>
            <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
              <SelectTrigger data-testid="tk-draft-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">{t("tk_tone_professional")}</SelectItem>
                <SelectItem value="formal">{t("tk_tone_formal")}</SelectItem>
                <SelectItem value="friendly">{t("tk_tone_friendly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Input data-testid="tk-draft-purpose" value={form.purpose} onChange={set("purpose")} placeholder={t("tk_draft_purpose_ph")} />
        <Textarea data-testid="tk-draft-points" value={form.points} onChange={set("points")} placeholder={t("tk_draft_points")} className="min-h-[120px]" />
        <RunButton onClick={run} loading={loading} testid="tk-draft-run" />
      </Panel>
      <Panel testid="tk-draft-output">
        {!res && <p className="text-sm text-muted-foreground">{t("tk_empty")}</p>}
        {res && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold" style={{ color: NAVY }}>{t("tk_subject")}</span>
              <CopyBtn text={`${subject}\n\n${body}`} />
            </div>
            <p className="text-sm font-medium text-foreground">{subject}</p>
            <div className="text-xs font-medium text-foreground mt-2">{t("tk_body")}</div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{body}</p>
            <SaveToCase kind="communication" title={subject} content={subject ? `${subject}\n\n${body}` : body} />
          </>
        )}
      </Panel>
    </div>
  );
}

/* ------------ Variance ------------ */
const VAR_FIELDS = ["standard_sales", "zero_rated_sales", "exempt_sales", "standard_purchases", "imports", "output_vat", "input_vat"];
function VarianceTool({ model }) {
  const { t, lang, fmt } = useI18n();
  const [cur, setCur] = useState({});
  const [prev, setPrev] = useState({});
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setLoading(true); setRes(null);
    try { const r = await api.post("/toolkit/variance", { current: cur, previous: prev, model }); setRes(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const label = (f) => ({ standard_sales: t("tk_f_standard_sales"), zero_rated_sales: t("tk_f_zero_sales"), exempt_sales: t("tk_f_exempt_sales"), standard_purchases: t("tk_f_standard_purchases"), imports: t("tk_f_imports"), output_vat: t("tk_output_vat"), input_vat: t("tk_input_vat") }[f] || f);
  const flagColor = { significant: "text-[hsl(354_60%_45%)]", watch: "text-[hsl(30_80%_38%)]", normal: "text-muted-foreground" };
  const checks = res && (lang === "ar" ? res.recommended_checks_ar : res.recommended_checks_en) || [];
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Panel testid="tk-var-current">
          <div className="text-sm font-semibold" style={{ color: GREEN }}>{t("tk_var_current")}</div>
          {VAR_FIELDS.map((f) => (
            <div key={f} className="flex items-center justify-between gap-2">
              <label className="text-xs text-muted-foreground flex-1">{label(f)}</label>
              <Input data-testid={`tk-var-cur-${f}`} type="number" className="w-40 h-9" value={cur[f] || ""} onChange={(e) => setCur({ ...cur, [f]: e.target.value })} />
            </div>
          ))}
        </Panel>
        <Panel testid="tk-var-previous">
          <div className="text-sm font-semibold text-muted-foreground">{t("tk_var_previous")}</div>
          {VAR_FIELDS.map((f) => (
            <div key={f} className="flex items-center justify-between gap-2">
              <label className="text-xs text-muted-foreground flex-1">{label(f)}</label>
              <Input data-testid={`tk-var-prev-${f}`} type="number" className="w-40 h-9" value={prev[f] || ""} onChange={(e) => setPrev({ ...prev, [f]: e.target.value })} />
            </div>
          ))}
        </Panel>
      </div>
      <RunButton onClick={run} loading={loading} testid="tk-var-run" />
      {res && (
        <Panel testid="tk-var-output">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold" style={{ color: NAVY }}>{t("tk_summary")}</span><ConfBadge level={res.confidence} /></div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{lang === "ar" ? res.summary_ar : res.summary_en}</p>
          {res.variances?.length > 0 && (
            <div className="mt-2 space-y-2">
              {res.variances.map((v, i) => (
                <div key={i} data-testid={`tk-var-row-${i}`} className="rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">{v.field}</span>
                    <span className={`text-xs font-semibold ${flagColor[v.flag] || flagColor.normal}`}>{v.flag} · {v.pct != null ? `${v.pct}%` : ""}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{fmt(v.previous)} → {fmt(v.current)} (Δ {fmt(v.delta)})</div>
                  <p className="text-xs text-muted-foreground mt-1">{lang === "ar" ? v.note_ar : v.note_en}</p>
                </div>
              ))}
            </div>
          )}
          {checks.length > 0 && (
            <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_var_checks")}</div>
              <ul className="text-sm text-muted-foreground mt-1 space-y-1">{checks.map((c, i) => <li key={i}>• {c}</li>)}</ul></div>
          )}
        </Panel>
      )}
    </div>
  );
}

/* ------------ Forecast ------------ */
const FC_FIELDS = ["standard_sales", "zero_rated_sales", "exempt_sales", "standard_purchases", "imports", "prior_credit"];
function ForecastTool({ model }) {
  const { t, lang, fmt } = useI18n();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setLoading(true); setRes(null);
    try { const r = await api.post("/toolkit/forecast", { ...form, model }); setRes(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const label = (f) => ({ standard_sales: t("tk_f_standard_sales"), zero_rated_sales: t("tk_f_zero_sales"), exempt_sales: t("tk_f_exempt_sales"), standard_purchases: t("tk_f_standard_purchases"), imports: t("tk_f_imports"), prior_credit: t("tk_f_prior_credit") }[f]);
  const posLabel = res && { payable: t("tk_pos_payable"), refundable: t("tk_pos_refundable"), nil: t("tk_pos_nil") }[res.position];
  const watch = res && (lang === "ar" ? res.watch_points_ar : res.watch_points_en) || [];
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel testid="tk-fc-input">
        {FC_FIELDS.map((f) => (
          <div key={f} className="flex items-center justify-between gap-2">
            <label className="text-xs text-muted-foreground flex-1">{label(f)}</label>
            <Input data-testid={`tk-fc-${f}`} type="number" className="w-44 h-9" value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
          </div>
        ))}
        <RunButton onClick={run} loading={loading} testid="tk-fc-run" />
      </Panel>
      <Panel testid="tk-fc-output">
        {!res && <p className="text-sm text-muted-foreground">{t("tk_empty")}</p>}
        {res && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-secondary p-3"><div className="text-[11px] text-muted-foreground">{t("tk_output_vat")}</div><div className="text-lg font-bold" style={{ color: NAVY }}>{fmt(res.output_vat)}</div></div>
              <div className="rounded-xl bg-secondary p-3"><div className="text-[11px] text-muted-foreground">{t("tk_input_vat")}</div><div className="text-lg font-bold" style={{ color: NAVY }}>{fmt(res.input_vat)}</div></div>
              <div className="rounded-xl p-3 col-span-2" style={{ backgroundColor: "hsl(158 64% 95%)" }}>
                <div className="text-[11px] text-muted-foreground">{t("tk_net_due")} · {posLabel}</div>
                <div className="text-2xl font-bold" style={{ color: GREEN }}>{fmt(res.net_vat_due)} <span className="text-xs font-normal">SAR</span></div>
              </div>
            </div>
            {(res.commentary_ar || res.commentary_en) && (
              <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_commentary")}</div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{lang === "ar" ? res.commentary_ar : res.commentary_en}</p></div>
            )}
            {watch.length > 0 && (
              <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_watch")}</div>
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">{watch.map((w, i) => <li key={i}>• {w}</li>)}</ul></div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}

/* ------------ Reconciliation ------------ */
const RECFLAG_COLOR = {
  vat_mismatch: "text-[hsl(354_60%_45%)]",
  invalid_tin: "text-[hsl(354_60%_45%)]",
  missing_tin: "text-[hsl(30_80%_38%)]",
  duplicate: "text-[hsl(30_80%_38%)]",
  zero_or_negative: "text-[hsl(30_80%_38%)]",
  missing_vat: "text-[hsl(30_80%_38%)]",
};
function LedgerCard({ title, s, fmt, t }) {
  if (!s) return null;
  return (
    <div className="rounded-xl border border-border p-3 space-y-2" data-testid="rec-ledger-card">
      <div className="text-sm font-semibold" style={{ color: NAVY }}>{title} · <span className="text-xs font-normal text-muted-foreground">{s.row_count} {t("rec_rows")}</span></div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-secondary p-2"><div className="text-[10px] text-muted-foreground">{t("rec_base")}</div><div className="font-bold text-sm">{fmt(s.total_base)}</div></div>
        <div className="rounded-lg bg-secondary p-2"><div className="text-[10px] text-muted-foreground">{t("rec_vat")}</div><div className="font-bold text-sm">{fmt(s.total_vat)}</div></div>
        <div className="rounded-lg bg-secondary p-2"><div className="text-[10px] text-muted-foreground">{t("rec_expected_vat")}</div><div className="font-bold text-sm">{fmt(s.expected_vat)}</div></div>
      </div>
      <div className="text-xs text-muted-foreground">{t("rec_gap")}: <span className={Math.abs(s.vat_gap) > 1 ? "font-semibold text-[hsl(354_60%_45%)]" : "font-medium text-foreground"}>{fmt(s.vat_gap)}</span></div>
      {s.flagged?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-foreground">{t("rec_flagged")} ({s.flagged.length})</div>
          <div className="max-h-56 overflow-auto mt-1 space-y-1">
            {s.flagged.map((f, i) => (
              <div key={i} data-testid={`rec-flag-${i}`} className="rounded-lg border border-border p-2 text-xs">
                <div className="flex justify-between gap-2"><span className="font-medium truncate">#{f.row} {f.invoice_no || ""}</span><span className="text-muted-foreground shrink-0">{fmt(f.base)} / {fmt(f.vat)}</span></div>
                <div className="mt-0.5 flex flex-wrap gap-x-2">{f.flags.map((fl, j) => <span key={j} className={`${RECFLAG_COLOR[fl] || "text-muted-foreground"} text-[10px]`}>• {t("recflag_" + fl)}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function ReconcileTool({ model }) {
  const { t, lang, fmt } = useI18n();
  const salesRef = useRef(null);
  const purchRef = useRef(null);
  const [salesFile, setSalesFile] = useState(null);
  const [purchFile, setPurchFile] = useState(null);
  const [salesName, setSalesName] = useState("");
  const [purchName, setPurchName] = useState("");
  const [declOut, setDeclOut] = useState("");
  const [declIn, setDeclIn] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!salesFile && !purchFile) { toast.error(t("rec_need_file")); return; }
    setLoading(true); setRes(null);
    const fd = new FormData();
    if (salesFile) fd.append("sales_file", salesFile);
    if (purchFile) fd.append("purchases_file", purchFile);
    if (declOut) fd.append("declared_output_vat", declOut);
    if (declIn) fd.append("declared_input_vat", declIn);
    if (model) fd.append("model", model);
    try { const r = await api.post("/toolkit/reconcile", fd, { headers: { "Content-Type": "multipart/form-data" } }); setRes(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const commentary = res && (lang === "ar" ? res.commentary_ar : res.commentary_en);
  const checks = (res && (lang === "ar" ? res.recommended_checks_ar : res.recommended_checks_en)) || [];
  const draftContent = res ? [commentary, ...checks.map((c) => "• " + c)].filter(Boolean).join("\n") : "";
  return (
    <div className="space-y-4" data-testid="tk-reconcile">
      <div className="rounded-xl border border-[hsl(217_63%_88%)] bg-[hsl(217_63%_97%)] px-4 py-2.5 text-xs text-[hsl(217_63%_30%)]">{t("rec_help")}</div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel testid="rec-input">
          <div className="space-y-2">
            <div className="text-sm font-semibold" style={{ color: NAVY }}>{t("rec_sales_ledger")}</div>
            <input ref={salesRef} type="file" accept=".csv,.xlsx,.xls" hidden data-testid="rec-sales-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setSalesFile(f); setSalesName(f.name); } }} />
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" className="rounded-full" data-testid="rec-sales-btn" onClick={() => salesRef.current?.click()}><Upload className="h-4 w-4 me-1.5" />{t("rec_choose")}</Button>
              {salesName && <span className="text-xs text-muted-foreground truncate max-w-[180px]">{salesName}</span>}
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="text-sm font-semibold" style={{ color: NAVY }}>{t("rec_purch_ledger")}</div>
            <input ref={purchRef} type="file" accept=".csv,.xlsx,.xls" hidden data-testid="rec-purch-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPurchFile(f); setPurchName(f.name); } }} />
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" className="rounded-full" data-testid="rec-purch-btn" onClick={() => purchRef.current?.click()}><Upload className="h-4 w-4 me-1.5" />{t("rec_choose")}</Button>
              {purchName && <span className="text-xs text-muted-foreground truncate max-w-[180px]">{purchName}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
            <div><label className="text-xs text-muted-foreground">{t("rec_decl_out")}</label><Input data-testid="rec-decl-out" type="number" value={declOut} onChange={(e) => setDeclOut(e.target.value)} className="h-9" /></div>
            <div><label className="text-xs text-muted-foreground">{t("rec_decl_in")}</label><Input data-testid="rec-decl-in" type="number" value={declIn} onChange={(e) => setDeclIn(e.target.value)} className="h-9" /></div>
          </div>
          <RunButton onClick={run} loading={loading} testid="rec-run" />
        </Panel>
        <Panel testid="rec-output">
          {!res && <p className="text-sm text-muted-foreground">{t("tk_empty")}</p>}
          {res && (
            <div className="space-y-3">
              <LedgerCard title={t("rec_sales_ledger")} s={res.sales} fmt={fmt} t={t} />
              <LedgerCard title={t("rec_purch_ledger")} s={res.purchases} fmt={fmt} t={t} />
              <div className="rounded-xl p-3" style={{ backgroundColor: "hsl(158 64% 95%)" }}>
                <div className="text-[11px] text-muted-foreground">{t("rec_net")}</div>
                <div className="text-xl font-bold" style={{ color: GREEN }} data-testid="rec-net-value">{fmt(res.net?.net_vat_due)} <span className="text-xs font-normal">SAR</span></div>
              </div>
              {res.declared && (
                <div className="rounded-xl border border-border p-3 text-xs space-y-1" data-testid="rec-declared">
                  <div className="font-medium text-foreground">{t("rec_vs_return")}</div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("rec_decl_out")}</span><span className={Math.abs(res.declared.output_variance) > 1 ? "font-semibold text-[hsl(354_60%_45%)]" : "font-medium"}>Δ {fmt(res.declared.output_variance)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("rec_decl_in")}</span><span className={Math.abs(res.declared.input_variance) > 1 ? "font-semibold text-[hsl(354_60%_45%)]" : "font-medium"}>Δ {fmt(res.declared.input_variance)}</span></div>
                </div>
              )}
              {commentary && (
                <div>
                  <div className="flex items-center justify-between"><span className="text-xs font-semibold" style={{ color: NAVY }}>{t("rec_commentary")}</span><ConfBadge level={res.confidence} /></div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{commentary}</p>
                </div>
              )}
              {checks.length > 0 && (
                <div><div className="text-xs font-medium text-foreground">{t("rec_checks")}</div>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">{checks.map((c, i) => <li key={i}>• {c}</li>)}</ul></div>
              )}
              <SaveToCase kind="reconcile" title={t("rec_title")} content={draftContent} />
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ------------ Advisor Bot ------------ */
function AdvSection({ icon: Icon, title, color, children }) {
  if (!children) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-semibold mt-2" style={{ color }}><Icon className="h-4 w-4" />{title}</div>
      <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap leading-relaxed">{children}</div>
    </div>
  );
}
function AdvisorTool({ model }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ question: "", amount: "", counterparty: "", country: "", sector_type: "private" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [tax, setTax] = useState("vat");
  const [precedents, setPrecedents] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const run = async () => {
    if (!form.question.trim()) return;
    setLoading(true); setRes(null); setPrecedents([]);
    try {
      const r = await api.post("/toolkit/advisor", { ...form, tax_type: tax, model }); setRes(r.data);
      try { const m = await api.get("/knowledge/match", { params: { q: form.question, tax, limit: 4 } }); setPrecedents(m.data.matches || []); } catch { /* non-blocking */ }
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const P = (ar, en) => (lang === "ar" ? ar : en);
  const facts = res && P(res.facts_understood_ar, res.facts_understood_en);
  const solution = res && P(res.potential_treatment_ar, res.potential_treatment_en);
  const detail = res && P(res.specialist_detail_ar, res.specialist_detail_en);
  const risks = res && P(res.risk_considerations_ar, res.risk_considerations_en);
  const docs = (res && res.required_documents) || [];
  const missing = (res && res.missing_facts) || [];
  const refs = (res && res.regulatory_references) || [];
  const draftContent = res ? [
    facts && `${t("tk_adv_facts")}: ${facts}`,
    solution && `${t("tk_adv_solution")}: ${solution}`,
    risks && `${t("tk_adv_risks")}: ${risks}`,
  ].filter(Boolean).join("\n\n") : "";
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel testid="tk-adv-input">
        <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">{t("tk_tax")}</span><TaxSelect value={tax} onChange={setTax} testid="tk-adv-tax" /></div>
        <Textarea data-testid="tk-adv-q" value={form.question} onChange={set("question")} placeholder={t("tk_adv_q_ph")} className="min-h-[150px]" />
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="tk-adv-amount" value={form.amount} onChange={set("amount")} placeholder={t("tk_adv_amount")} />
          <Input data-testid="tk-adv-counterparty" value={form.counterparty} onChange={set("counterparty")} placeholder={t("tk_adv_counterparty")} />
          <Input data-testid="tk-adv-country" value={form.country} onChange={set("country")} placeholder={t("tk_adv_country")} />
          <Select value={form.sector_type} onValueChange={(v) => setForm({ ...form, sector_type: v })}>
            <SelectTrigger data-testid="tk-adv-sector"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="private">{t("tk_sector_private")}</SelectItem>
              <SelectItem value="government">{t("tk_sector_government")}</SelectItem>
              <SelectItem value="nonprofit">{t("tk_sector_nonprofit")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <RunButton onClick={run} loading={loading} testid="tk-adv-run" />
      </Panel>
      <Panel testid="tk-adv-output">
        {!res && <p className="text-sm text-muted-foreground">{t("tk_empty")}</p>}
        {res && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold" style={{ color: NAVY }}>{t("tk_tab_advisor")}</span>
              <div className="flex items-center gap-3"><ConfBadge level={res.confidence} /><CopyBtn text={draftContent} /></div>
            </div>
            <AdvSection icon={FileText} title={t("tk_adv_facts")} color="hsl(217 63% 25%)">{facts}</AdvSection>
            {refs.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold mt-2" style={{ color: GREEN }}><Scale className="h-4 w-4" />{t("tk_adv_basis")}</div>
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">{refs.map((r, i) => <li key={i}>• {r.article} — {lang === "ar" ? r.title_ar : r.title_en}</li>)}</ul>
              </div>
            )}
            <AdvSection icon={Lightbulb} title={t("tk_adv_solution")} color="hsl(38 80% 38%)">{solution}</AdvSection>
            <AdvSection icon={Sparkles} title={t("tk_adv_detail")} color="hsl(262 60% 45%)">{detail}</AdvSection>
            <AdvSection icon={ShieldAlert} title={t("tk_adv_risks")} color="hsl(354 60% 45%)">{risks}</AdvSection>
            {missing.length > 0 && (
              <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_adv_missing")}</div>
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">{missing.map((m, i) => <li key={i}>• {m}</li>)}</ul></div>
            )}
            {docs.length > 0 && (
              <div><div className="text-xs font-medium text-foreground mt-2">{t("tk_adv_required_docs")}</div>
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">{docs.map((d, i) => <li key={i}>• {d}</li>)}</ul></div>
            )}
            {precedents.length > 0 && (
              <div data-testid="tk-adv-precedents">
                <div className="flex items-center gap-1.5 text-xs font-semibold mt-2" style={{ color: "hsl(217 63% 25%)" }}><Scale className="h-4 w-4" />{t("tk_adv_precedents")}</div>
                <ul className="mt-1 space-y-1.5">
                  {precedents.map((p, i) => (
                    <li key={i} className="rounded-lg border border-border p-2.5">
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[hsl(158_64%_28%)] hover:underline">{p.reference}</a>
                      <p className="text-xs text-muted-foreground mt-0.5" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.title_ar}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <SaveToCase kind="research" title={form.question.slice(0, 80)} content={draftContent} />
          </>
        )}
      </Panel>
    </div>
  );
}

function ModelSelect({ models, model, setModel, lang }) {
  if (!models.length) return null;
  return (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="tk-model-select-wrap">
      <span className="text-xs text-muted-foreground">{lang === "ar" ? "النموذج" : "Model"}</span>
      <Select value={model} onValueChange={setModel}>
        <SelectTrigger data-testid="tk-model-select" className="w-44 h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          {models.map((m) => (
            <SelectItem key={m.id} value={m.id} data-testid={`tk-model-opt-${m.id}`}>
              {lang === "ar" ? (m.label_ar || m.label) : m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function AIToolkit() {
  const { t, lang } = useI18n();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  useEffect(() => {
    api.get("/toolkit/models").then((r) => {
      setModels(r.data.models || []);
      setModel(r.data.default || (r.data.models?.[0]?.id ?? ""));
    }).catch(() => {});
  }, []);
  return (
    <div className="space-y-5 gc-fade-up" data-testid="ai-toolkit">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: NAVY }}><Sparkles className="h-5 w-5 text-white" /></div>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold" style={{ color: NAVY }}>{t("tk_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("tk_sub")}</p>
        </div>
        <ModelSelect models={models} model={model} setModel={setModel} lang={lang} />
        <a href="/sop.html" target="_blank" rel="noreferrer" data-testid="tk-sop-link"
          className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-border px-3 py-2 hover:bg-secondary transition-colors shrink-0" style={{ color: NAVY }}>
          <BookOpen className="h-4 w-4" /> {t("tk_sop")}
        </a>
      </div>
      <div className="rounded-xl border border-[hsl(38_92%_80%)] bg-[hsl(38_92%_96%)] px-4 py-2.5 text-xs text-[hsl(30_80%_35%)] flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {t("tk_disclaimer")}
      </div>

      <Tabs defaultValue="advisor">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="advisor" data-testid="tk-tab-advisor"><Scale className="h-4 w-4 me-1.5" />{t("tk_tab_advisor")}</TabsTrigger>
          <TabsTrigger value="research" data-testid="tk-tab-research"><Search className="h-4 w-4 me-1.5" />{t("tk_tab_research")}</TabsTrigger>
          <TabsTrigger value="document" data-testid="tk-tab-document"><FileSearch className="h-4 w-4 me-1.5" />{t("tk_tab_document")}</TabsTrigger>
          <TabsTrigger value="draft" data-testid="tk-tab-draft"><Mail className="h-4 w-4 me-1.5" />{t("tk_tab_draft")}</TabsTrigger>
          <TabsTrigger value="variance" data-testid="tk-tab-variance"><GitCompareArrows className="h-4 w-4 me-1.5" />{t("tk_tab_variance")}</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tk-tab-forecast"><Calculator className="h-4 w-4 me-1.5" />{t("tk_tab_forecast")}</TabsTrigger>
          <TabsTrigger value="reconcile" data-testid="tk-tab-reconcile"><FileSpreadsheet className="h-4 w-4 me-1.5" />{t("tk_tab_reconcile")}</TabsTrigger>
        </TabsList>
        <TabsContent value="advisor" className="mt-4"><AdvisorTool model={model} /></TabsContent>
        <TabsContent value="research" className="mt-4"><ResearchTool model={model} /></TabsContent>
        <TabsContent value="document" className="mt-4"><DocumentTool model={model} /></TabsContent>
        <TabsContent value="draft" className="mt-4"><DraftTool model={model} /></TabsContent>
        <TabsContent value="variance" className="mt-4"><VarianceTool model={model} /></TabsContent>
        <TabsContent value="forecast" className="mt-4"><ForecastTool model={model} /></TabsContent>
        <TabsContent value="reconcile" className="mt-4"><ReconcileTool model={model} /></TabsContent>
      </Tabs>
    </div>
  );
}
